import { PermissionGroupItem, UiStatus } from './aiAssistantModel'

/**
 * Row state and copy for the consolidated approval card (chat#169).
 *
 * Kept out of the component so the branching a partial failure introduces —
 * which rows still need a decision, whether Retry is still on the table, what
 * the card's outcome was overall — is testable without rendering.
 */

export type RowPartition = {
  /** Rows the group decision still governs: never run, or failed and retryable. */
  outstanding: PermissionGroupItem[]
  /** Rows that are done, shown for context but offering no controls. */
  settled: PermissionGroupItem[]
}

export function partitionRows(rows: PermissionGroupItem[]): RowPartition {
  return {
    outstanding: rows.filter((r) => r.status === 'pending' || r.status === 'failed'),
    settled: rows.filter((r) => r.status === 'applied' || r.status === 'declined'),
  }
}

/**
 * Whether the card has nothing left to ask.
 *
 * A failed row does *not* count as decided: it is waiting on a retry-or-cancel
 * that the user has not given yet. An empty group is treated as undecided
 * rather than complete — a card with no rows is a bug, and claiming it finished
 * would hide it.
 */
export function isGroupDecided(rows: PermissionGroupItem[]): boolean {
  return rows.length > 0 && partitionRows(rows).outstanding.length === 0
}

/**
 * Whether to offer Retry.
 *
 * Only for a failure the server will actually re-run. Once every failed row has
 * burned its attempts the button would post a decision the server declines to
 * act on, so the card drops it and leaves Cancel as the way out.
 */
export function showRetry(rows: PermissionGroupItem[]): boolean {
  return rows.some((r) => r.status === 'failed' && r.retryable)
}

export function groupCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'change' : 'changes'}`
}

/** The badge for a settled row, or null while it still awaits a decision. */
export function rowStatusLabel(status: PermissionGroupItem['status']): string | null {
  switch (status) {
    case 'applied':
      return 'Applied'
    case 'failed':
      return 'Failed'
    case 'declined':
      return 'Cancelled'
    default:
      return null
  }
}

/**
 * Whether the card has nothing left to ask *or* is being replayed from history.
 *
 * A replayed card must show its outcome rather than dead controls, and its rows
 * can still read `failed` — the server records the failure, and the user's
 * cancel is what closed the card. Judging by the rows alone would put a disabled
 * Retry button on a conversation that ended days ago.
 */
export function isGroupSettled(
  rows: PermissionGroupItem[],
  historicalStatus: UiStatus | undefined
): boolean {
  if (
    historicalStatus === 'approved' ||
    historicalStatus === 'denied' ||
    historicalStatus === 'partially_applied'
  ) {
    return true
  }
  return isGroupDecided(rows)
}

/**
 * The card's overall outcome once settled.
 *
 * A run where anything failed or was abandoned is not an approval, and saying
 * so would misreport what happened to the data — the whole point of the card is
 * that the user can tell. "Approved" is reserved for every row landing.
 */
export function groupOutcomeLabel(rows: PermissionGroupItem[]): string {
  const applied = rows.some((r) => r.status === 'applied')
  const unapplied = rows.some((r) => r.status === 'declined' || r.status === 'failed')
  if (applied && unapplied) {
    return 'Partly applied'
  }
  if (applied) {
    return 'Approved'
  }
  return rows.some((r) => r.status === 'failed') ? 'Failed' : 'Denied'
}

/**
 * The badge colour for that same outcome.
 *
 * Lives here rather than in the component because it encodes the same
 * classification {@link groupOutcomeLabel} does and has to agree with it: a card
 * reading "Approved" in a warning colour, or "Partly applied" in a success one,
 * misreports what happened to the data at the glance that matters most on a
 * replayed transcript. Keeping the pair side by side puts them under one set of
 * tests, so a new outcome added to one cannot silently drift from the other.
 */
export function outcomeTone(rows: PermissionGroupItem[]): 'good' | 'bad' | 'muted' {
  if (rows.some((r) => r.status === 'failed')) {
    return 'bad'
  }
  return rows.every((r) => r.status === 'applied') ? 'good' : 'muted'
}
